import { Link } from 'react-router-dom';
import { Minus, Plus, Trash, ArrowRight, ShieldCheck } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { motion } from 'framer-motion';

export default function CartPage() {
  const { cart, updateQuantity, removeFromCart, cartSubtotal, loading } = useCart();
  const shippingCost = cartSubtotal >= 75 ? 0 : 9.99;
  const total = cartSubtotal + shippingCost;

  if (cart.items.length === 0) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 text-center" data-testid="empty-cart">
        <h1 className="font-serif text-4xl font-medium text-nexa-charcoal mb-4">Your Cart is Empty</h1>
        <p className="text-muted-foreground mb-8">Looks like you haven't added anything to your cart yet.</p>
        <Link to="/shop">
          <Button className="btn-primary" data-testid="continue-shopping-btn">
            Continue Shopping
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12" data-testid="cart-page">
      <h1 className="font-serif text-4xl font-medium text-nexa-charcoal mb-8">Shopping Cart</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-6" data-testid="cart-items">
          {cart.items.map((item, index) => (
            <motion.div
              key={item.product_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex gap-6 p-6 bg-white rounded-lg border border-nexa-border"
              data-testid={`cart-item-${item.product_id}`}
            >
              <Link to={`/product/${item.product_id}`} className="flex-shrink-0">
                <img 
                  src={item.product?.images?.[0] || ''} 
                  alt={item.product?.name}
                  className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-md"
                />
              </Link>

              <div className="flex-1 min-w-0">
                <div className="flex justify-between gap-4">
                  <div>
                    <Link 
                      to={`/product/${item.product_id}`}
                      className="font-medium text-nexa-charcoal hover:text-nexa-gold transition-colors line-clamp-1"
                    >
                      {item.product?.name}
                    </Link>
                    <p className="text-sm text-muted-foreground mt-1">{item.product?.category}</p>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.product_id)}
                    className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                    disabled={loading}
                    data-testid={`remove-item-${item.product_id}`}
                  >
                    <Trash size={20} />
                  </button>
                </div>

                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center border border-nexa-border rounded-md">
                    <button
                      onClick={() => updateQuantity(item.product_id, Math.max(1, item.quantity - 1))}
                      className="quantity-btn"
                      disabled={loading}
                      data-testid={`decrease-qty-${item.product_id}`}
                    >
                      <Minus size={14} />
                    </button>
                    <span className="w-10 text-center text-sm font-medium">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                      className="quantity-btn"
                      disabled={loading}
                      data-testid={`increase-qty-${item.product_id}`}
                    >
                      <Plus size={14} />
                    </button>
                  </div>

                  <p className="font-serif text-lg font-semibold text-nexa-charcoal">
                    ${((item.product?.price || 0) * item.quantity).toFixed(2)}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-nexa-border p-6 sticky top-32" data-testid="order-summary">
            <h2 className="font-serif text-xl font-semibold text-nexa-charcoal mb-6">Order Summary</h2>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-medium text-nexa-charcoal">${cartSubtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Shipping</span>
                <span className="font-medium text-nexa-charcoal">
                  {shippingCost === 0 ? (
                    <span className="text-green-600">FREE</span>
                  ) : (
                    `$${shippingCost.toFixed(2)}`
                  )}
                </span>
              </div>
              {cartSubtotal < 75 && (
                <p className="text-sm text-nexa-gold">
                  Add ${(75 - cartSubtotal).toFixed(2)} more for free shipping!
                </p>
              )}
            </div>

            <div className="border-t border-nexa-border pt-4 mb-6">
              <div className="flex justify-between">
                <span className="font-semibold text-nexa-charcoal">Total</span>
                <span className="font-serif text-2xl font-semibold text-nexa-charcoal">${total.toFixed(2)}</span>
              </div>
            </div>

            <Link to="/checkout">
              <Button className="w-full h-14 btn-gold mb-4" data-testid="proceed-to-checkout">
                Proceed to Checkout
              </Button>
            </Link>

            <Link to="/shop">
              <Button variant="outline" className="w-full h-12 border-nexa-charcoal text-nexa-charcoal hover:bg-nexa-charcoal hover:text-white">
                Continue Shopping
              </Button>
            </Link>

            <div className="flex items-center justify-center gap-2 mt-6 text-sm text-muted-foreground">
              <ShieldCheck size={18} className="text-nexa-gold" />
              <span>Secure Checkout</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
